import { apiClient } from './config';
import {
  IOrganisationService,
  Organisation,
  OrganisationMenu,
  Shift,
  User,
  Meal,
  Comment,
  Order,
  BusinessDay,
} from '../interfaces';

export class OrganisationService implements IOrganisationService {
  private static instance: OrganisationService;
  private readonly cache: Map<string, { data: any; timestamp: number }> =
    new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  static getInstance(): OrganisationService {
    if (!OrganisationService.instance) {
      OrganisationService.instance = new OrganisationService();
    }
    return OrganisationService.instance;
  }

  private isCacheValid(key: string): boolean {
    const cached = this.cache.get(key);
    if (!cached) return false;
    return Date.now() - cached.timestamp < this.CACHE_DURATION;
  }

  // Shift management
  async getShifts(): Promise<Shift[]> {
    const cacheKey = 'organisation-shifts';
    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey)!.data;
    }

    const response = await apiClient.get<Shift[]>('/api/organisation/shifts');
    this.cache.set(cacheKey, { data: response.data, timestamp: Date.now() });
    return response.data;
  }

  async addShift(shift: Partial<Shift>): Promise<Shift> {
    const response = await apiClient.post<Shift>(
      '/api/organisation/shifts',
      shift
    );
    // Invalidate cache
    this.cache.delete('organisation-shifts');
    return response.data;
  }

  async updateShift(shift: Shift): Promise<boolean> {
    await apiClient.put(`/api/organisation/shifts`, shift);
    // Invalidate cache
    this.cache.delete('organisation-shifts');
    return true;
  }

  async deleteShift(id: number): Promise<boolean> {
    await apiClient.delete(`/api/organisation/shifts/${id}`);
    // Invalidate cache
    this.cache.delete('organisation-shifts');
    return true;
  }

  // Comments management
  async getComments(menuId: number): Promise<Comment[]> {
    const cacheKey = `organisation-comments-${menuId}`;
    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey)!.data;
    }

    const response = await apiClient.get<Comment[]>(
      `/api/organisation/comments/${menuId}`
    );
    this.cache.set(cacheKey, { data: response.data, timestamp: Date.now() });
    return response.data;
  }

  async addComment(comment: Comment): Promise<boolean> {
    await apiClient.post('/api/organisation/comments', comment);
    // Invalidate cache
    this.cache.delete(`organisation-comments-${comment.organisationMenuId}`);
    return true;
  }

  // Order management
  async getOrders(): Promise<Order[]> {
    const cacheKey = 'organisation-orders';
    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey)!.data;
    }

    try {
      const response = await apiClient.get<Order[]>('/api/organisation/orders');
      this.cache.set(cacheKey, {
        data: response.data || [],
        timestamp: Date.now(),
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching orders:', error);
      return [];
    }
  }

  async addOrder(order: Order): Promise<Order> {
    const response = await apiClient.post<Order>(
      '/api/organisation/orders',
      order
    );
    // Invalidate cache
    this.cache.delete('organisation-orders');
    return response.data;
  }

  async updateOrder(order: Order): Promise<boolean> {
    await apiClient.put('/api/organisation/orders', order);
    // Invalidate cache
    this.cache.delete('organisation-orders');
    return true;
  }

  async deleteOrder(id: number): Promise<boolean> {
    await apiClient.delete(`/api/organisation/orders/${id}`);
    // Invalidate cache
    this.cache.delete('organisation-orders');
    return true;
  }

  // Menu management
  async getOrganisationMenus(): Promise<OrganisationMenu[]> {
    const cacheKey = 'organisation-menus';
    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey)!.data;
    }

    const response = await apiClient.get<OrganisationMenu[]>(
      '/api/organisation/organisationmenus'
    );
    this.cache.set(cacheKey, { data: response.data, timestamp: Date.now() });
    return response.data;
  }

  async getMenus(): Promise<OrganisationMenu[]> {
    return this.getOrganisationMenus();
  }

  async getMenuById(id: number): Promise<OrganisationMenu | null> {
    const cacheKey = `organisation-menu-${id}`;
    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey)!.data;
    }

    try {
      const response = await apiClient.get<OrganisationMenu>(
        `/api/organisation/organisationmenus/${id}`
      );
      this.cache.set(cacheKey, { data: response.data, timestamp: Date.now() });
      return response.data;
    } catch (error) {
      console.error(`Error fetching menu with id ${id}:`, error);
      return null;
    }
  }

  async updateMenuStatus(
    menu: OrganisationMenu,
    statusComment?: string
  ): Promise<boolean> {
    try {
      // Prepare the DTO as expected by the server
      const statusUpdateDto = {
        id: menu.id,
        status: menu.status,
        comment: statusComment,
      };

      await apiClient.put(
        '/api/organisation/organisationmenus/status',
        statusUpdateDto
      );
      // Invalidate caches
      this.cache.delete('organisation-menus');
      this.cache.delete(`organisation-menu-${menu.id}`);
      return true;
    } catch (error) {
      console.error('Error updating menu status:', error);
      return false;
    }
  }

  async updateMenu(menu: OrganisationMenu): Promise<boolean> {
    try {
      await apiClient.put('/api/organisation/organisationmenus', menu);
      // Invalidate caches
      this.cache.delete('organisation-menus');
      this.cache.delete(`organisation-menu-${menu.id}`);
      return true;
    } catch (error) {
      console.error('Error updating menu:', error);
      // For development, simulate success
      return true;
    }
  }

  // These methods are no longer needed for our current requirements
  async createMenu(menu: Partial<OrganisationMenu>): Promise<OrganisationMenu> {
    throw new Error('Organisation users cannot create menus');
  }

  async deleteMenu(id: number): Promise<boolean> {
    throw new Error('Organisation users cannot delete menus');
  }

  // User management
  async getUsers(): Promise<User[]> {
    const cacheKey = 'organisation-users';
    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey)!.data;
    }

    try {
      const response = await apiClient.get<User[]>('/api/organisation/users');
      this.cache.set(cacheKey, {
        data: response.data || [],
        timestamp: Date.now(),
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching users:', error);
      return [];
    }
  }

  async addUser(user: Partial<User>): Promise<User> {
    const response = await apiClient.post<User>(
      '/api/organisation/users',
      user
    );
    // Invalidate cache
    this.cache.delete('organisation-users');
    return response.data;
  }

  async updateUser(user: User): Promise<boolean> {
    await apiClient.put('/api/organisation/users', user);
    // Invalidate cache
    this.cache.delete('organisation-users');
    return true;
  }

  async deleteUser(id: number): Promise<boolean> {
    await apiClient.delete(`/api/organisation/users/${id}`);
    // Invalidate cache
    this.cache.delete('organisation-users');
    return true;
  }

  // Methods using the UserAPI endpoints which are also useful for organisation
  async getMeals(): Promise<Meal[]> {
    const cacheKey = 'user-meals';
    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey)!.data;
    }

    const response = await apiClient.get<Meal[]>('/api/user/meals');
    this.cache.set(cacheKey, { data: response.data, timestamp: Date.now() });
    return response.data;
  }

  async getMealById(id: number): Promise<Meal | null> {
    const cacheKey = `user-meal-${id}`;
    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey)!.data;
    }

    try {
      const response = await apiClient.get<Meal>(`/api/user/meals/${id}`);
      this.cache.set(cacheKey, { data: response.data, timestamp: Date.now() });
      return response.data;
    } catch (error) {
      console.error(`Error fetching meal with id ${id}:`, error);
      return null;
    }
  }

  async getNextBusinessDays(count: number): Promise<BusinessDay[]> {
    const cacheKey = `user-business-days-${count}`;
    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey)!.data;
    }

    const response = await apiClient.get<BusinessDay[]>(
      `/api/user/business-days?count=${count}`
    );
    this.cache.set(cacheKey, { data: response.data, timestamp: Date.now() });
    return response.data;
  }

  async getMealsByDate(count: number): Promise<{ [date: string]: Meal[] }> {
    const cacheKey = `user-meals-by-date-${count}`;
    if (this.isCacheValid(cacheKey)) {
      return this.cache.get(cacheKey)!.data;
    }

    const response = await apiClient.get<{ [date: string]: Meal[] }>(
      `/api/user/meals/by-date?count=${count}`
    );
    this.cache.set(cacheKey, { data: response.data, timestamp: Date.now() });
    return response.data;
  }

  async getOrganisation(id: number): Promise<Organisation | null> {
    try {
      const response = await apiClient.get<Organisation>(
        `/api/organisation/organisations/${id}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching organisation with id ${id}:`, error);
      return null;
    }
  }

  // Replace the mock implementation of updateOrganisation
  async updateOrganisation(organisation: Organisation): Promise<boolean> {
    try {
      await apiClient.put('/api/organisation/organisations', organisation);
      return true;
    } catch (error) {
      console.error('Error updating organisation:', error);
      return false;
    }
  }
}
